import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TrackerCandidateStatus} from '../../../model';
import {trackerCandidateStatusForId} from '../../../utils';
import {AnalyticsService} from '../../shared/analytics.service';
import {HttpError} from '../../shared/api.service';
import {AuthService} from '../../shared/auth.service';
import {IdentifyModalComponent} from '../../shared/identify-modal/identify-modal.component';
import {NavigationService} from '../../shared/navigation.service';
import {ClientApiService} from '../client-api.service';

@Component({
  selector: 'candidate-summary',
  templateUrl: './candidate-summary.component.html',
  styleUrls: ['./candidate-summary.component.scss']
})
export class CandidateSummaryComponent implements OnChanges {

  @Input() submittal: Submittal;
  @Input() staff: StaffInfo[];
  @Input() favorites: string[];

  @Output() update = new EventEmitter<void>();

  favorite: boolean;
  saving: boolean;
  user$ = this.Auth.user$;
  masquerading$ = this.Auth.masquerading$;

  @ViewChild('identifyModal') identifyModal: IdentifyModalComponent;
  identifyCallback: Function | null = null;

  constructor(private Api: ClientApiService,
              private Auth: AuthService,
              private route: ActivatedRoute,
              private Router: Router,
              private Analytics: AnalyticsService,
              private Navigation: NavigationService) {
    this.resetState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.favorites && this.submittal.personId) {
      this.favorite = this.favorites.indexOf(this.submittal.personId) !== -1;
    }
  }

  resetState(): void {
    this.saving = false;
  }

  isNotInterested() {
    return this.submittal && trackerCandidateStatusForId(this.submittal.statusId) === TrackerCandidateStatus.notInterested;
  }

  goToProfile() {
    const returnTo = this.route.snapshot.queryParams['returnTo'];

    this.Router.navigate(['positions', this.submittal.orderId, 'candidates', this.submittal.id], {
      queryParams: {
        favorite: this.favorite,
        review: true,
        returnTo: returnTo && returnTo.split(',').concat('candidates').join(',')
      }
    });
  }

  ctaComplete() {
    this.update.emit();
  }

  toggleFavorite(event?: Event): Promise<void> {
    if (event) {
      event.stopPropagation();
    }
    if (this.masquerading$.getValue()) {
      return;
    }

    if (!this.Auth.isFullyAuthenticated()) {
      this.identifyCallback = () => {
        if (this.Auth.isFullyAuthenticated()) {
          this.Api.getFavorites()
            .subscribe(favorites => {
              if (favorites && this.submittal.personId) {
                this.favorite = favorites.includes(this.submittal.personId);
                if (!this.favorite) {
                  this.toggleFavorite()
                    .then(() => this.update.emit());
                }
              }
            }, (err: HttpError) => this.Navigation.handleError(err));
        }
      };
      this.identifyModal.show();
      return;
    }

    if (this.saving) {
      return;
    }
    this.favorite = !this.favorite;
    this.Analytics.trackEvent('Candidate favorite/unfavorite clicked', {category: 'Action'});
    this.saving = true;
    return (this.favorite ? this.Api.addFavorite : this.Api.removeFavorite)
      .call(this.Api, this.submittal.personId)
      .toPromise()
      .then(() => {
          this.Analytics.trackEvent('Candidate favorite/unfavorite succeeded');
          this.saving = false;
        },
        (err: HttpError) => {
          console.error('Failed to update favorites.', err);
          this.Analytics.trackEvent('Candidate favorite/unfavorite failed', {category: 'Error'});
          // FIXME display error
          // this.Translate.instant('talent-pool.favorite-retrieve-error', {error: err.code});
          this.favorite = !this.favorite;
          this.saving = false;
        });
  }

  showIdentification(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.identifyModal.show();
  }
}
